/**
 * /account/security — the signed-in user's own two-step sign-in ("Sign-in security", from the user menu; backend
 * aidocs/plans/2026-10-20-dashboard-password-mfa.md §3.4, §3.7, §3.8, §3.13; contract apiv2/dashboard_mfa.openapi.yaml).
 * Self only, no permission; calls in ./securityApi.js.
 *
 * - The factors by kind (the masked number for text messages); set up the other one, or replace one. Setting up or
 *   replacing needs the current password, then the new factor's first code. That confirm signs the session in
 *   again: the answer's token replaces this session's in one write (replaceSessionToken), with no sign-out, and a
 *   first factor brings the recovery codes.
 * - The default method (when both are set up), new recovery codes (needs a current code; text-message users get
 *   one by password), and turning it off where the tenant allows it (`can_disable`), which ends every session.
 * - The policy: Optional, Mandatory (also by the franchise parent), or SSO-only (nothing to set up).
 */
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { DataAPIContext } from '../api';
import { PhoneField, TotpSetup } from '../auth/MfaEnrollStep';
import { CodeField, codeReady, METHOD_LABELS, useCountdown } from '../auth/mfaUi';
import RecoveryCodesStep from '../auth/RecoveryCodesStep';
import { noteSignOutReason } from '../auth/signOutReason';
import PageMeta from '../components/PageMeta';
import { ContextStore } from '../store/contextStore';
import {
    MfaApiError,
    confirmFactor,
    disableMfa,
    getMfaStatus,
    regenerateRecoveryCodes,
    sendSmsCode,
    setPrimaryMethod,
    startFactor,
} from './securityApi';

export const TURNED_OFF_NOTICE = 'Two-step sign-in is off. Sign in again with your password.';

const KIND_LABEL = { totp: 'Authenticator app', sms: 'Text messages' };

const fmt = (ts) => (ts ? moment(ts).format('D MMM YYYY') : null);

const enrolledKinds = (status) => ['totp', 'sms'].filter((k) => (status.devices || []).some((d) => d.kind === k));

const policyText = (status) => {
    if (status.policy?.mode === 'mandatory') {
        return status.policy.inherited
            ? 'Your company\'s franchise requires two-step sign-in: every password sign-in asks for a code. It cannot be turned off.'
            : 'Your company requires two-step sign-in: every password sign-in asks for a code. It cannot be turned off.';
    }
    return status.enrolled
        ? 'Two-step sign-in is on: every password sign-in asks for a code. Your company leaves it optional.'
        : 'Two-step sign-in is optional for your company. Turn it on and every password sign-in also asks for a code from your phone.';
};

const PasswordField = ({ value, onChange, error, disabled, autoFocus }) => (
    <TextField
        fullWidth
        variant="outlined"
        margin="normal"
        type="password"
        label="Current password"
        autoComplete="current-password"
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        error={!!error}
        helperText={error || ' '}
        disabled={disabled}
        autoFocus={autoFocus}
    />
);

// ---- set up / replace a factor ----------------------------------------------------------------------------

const factorTitle = (kind, replacing) => {
    if (kind === 'sms') return replacing ? 'Change the phone number' : 'Set up text messages';
    return replacing ? 'Replace the authenticator app' : 'Set up an authenticator app';
};

function FactorDialog({ kind, replacing, phoneRegion, onClose, onConfirmed }) {
    const dataAPI = React.useContext(DataAPIContext);
    const [password, setPassword] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [device, setDevice] = React.useState(null);
    const [code, setCode] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [fieldErrors, setFieldErrors] = React.useState({});
    const [resendIn, startResend] = useCountdown();

    const fail = (err) => {
        const code_ = err instanceof MfaApiError ? err.code : null;
        if (code_ === 'invalid_password') {
            setFieldErrors({ password: err.message });
        } else if (code_ === 'invalid_phone') {
            setFieldErrors({ phone: err.message });
        } else {
            setError(err.message);
        }
        if (code_ === 'sms_cooldown') {
            startResend(err.body?.retry_after);
        }
    };

    const start = async () => {
        setBusy(true);
        setError(null);
        setFieldErrors({});
        try {
            const answer = await startFactor(dataAPI, kind, { password, phone });
            setDevice(answer);
            setCode('');
            if (kind === 'sms') {
                startResend(answer?.resend_after);
            }
        } catch (err) {
            fail(err);
        }
        setBusy(false);
    };

    const confirm = async () => {
        setBusy(true);
        setError(null);
        try {
            const result = await confirmFactor(dataAPI, kind, device.device_id, code);
            onConfirmed(result, kind);
            return;
        } catch (err) {
            // Five wrong codes (or a stale step) delete the pending device: step 1 again.
            if (err.status === 404 || err.body?.attempts_left === 0) {
                setDevice(null);
            }
            setCode('');
            fail(err);
        }
        setBusy(false);
    };

    const canStart = password.length > 0 && (kind !== 'sms' || (phone || '').replace(/\D/g, '').length >= 7);
    let resendLabel = 'Resend code';
    if (resendIn > 0) resendLabel = `Resend in ${resendIn} s`;

    return (
        <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="mfa-factor-title">
            <DialogTitle id="mfa-factor-title">{factorTitle(kind, replacing)}</DialogTitle>
            <DialogContent>
                {replacing && (
                    <Typography variant="body2" color="text.secondary">
                        {kind === 'sms' ? 'Your current number' : 'Your current authenticator'} keeps working until the new one is confirmed.
                    </Typography>
                )}
                {!device && (
                    <>
                        <PasswordField value={password} onChange={setPassword} error={fieldErrors.password} disabled={busy} autoFocus />
                        {kind === 'sms' && <PhoneField region={phoneRegion} value={phone} onChange={setPhone} error={fieldErrors.phone} disabled={busy} />}
                    </>
                )}
                {device && kind === 'totp' && <TotpSetup device={device} />}
                {device && kind === 'sms' && (
                    <Typography variant="body2" color="text.secondary">
                        We sent a code to {device.sent_to_masked}. It works for 5 minutes.
                    </Typography>
                )}
                {device && <CodeField value={code} onChange={setCode} autoFocus />}
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                {device && kind === 'sms' && (
                    <Button onClick={start} disabled={busy || resendIn > 0}>{resendLabel}</Button>
                )}
                {!device && (
                    <Button variant="contained" onClick={start} disabled={busy || !canStart}>
                        {kind === 'sms' ? 'Send code' : 'Continue'}
                    </Button>
                )}
                {device && (
                    <Button variant="contained" onClick={confirm} disabled={busy || !codeReady(code)}>Confirm</Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

// ---- a current code: new recovery codes, turning it off ------------------------------------------------------

// The method picker and code field both dialogs need. A texted code needs the password to be sent.
function useCurrentCode(status) {
    const kinds = enrolledKinds(status);
    const [method, setMethod] = React.useState(kinds.includes(status.primary_method) ? status.primary_method : kinds[0]);
    const [code, setCode] = React.useState('');
    const [sentTo, setSentTo] = React.useState(null);
    const [resendIn, startResend] = useCountdown();
    return { kinds, method, setMethod, code, setCode, sentTo, setSentTo, resendIn, startResend };
}

// `passwordField`: shown under the method picker for a texted code (the dialog that always needs the password
// shows its own above).
function CurrentCodeFields({ current, password, passwordField = null, sending, onSend, disabled }) {
    const { kinds, method, setMethod, code, setCode, sentTo, resendIn } = current;
    let sendLabel = sentTo ? 'Resend code' : 'Send code';
    if (resendIn > 0) sendLabel = `${sentTo ? 'Resend' : 'Send'} in ${resendIn} s`;
    return (
        <>
            {kinds.length > 1 && (
                <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <FormLabel component="legend">Code from</FormLabel>
                    <RadioGroup
                        row
                        value={method}
                        onChange={(ev) => {
                            setMethod(ev.target.value);
                            setCode('');
                        }}
                    >
                        {kinds.map((k) => (
                            <FormControlLabel key={k} value={k} control={<Radio size="small" />} label={METHOD_LABELS[k]} disabled={disabled} />
                        ))}
                    </RadioGroup>
                </FormControl>
            )}
            {method === 'sms' && passwordField}
            {method === 'sms' && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Button variant="outlined" onClick={onSend} disabled={disabled || sending || resendIn > 0 || !password}>{sendLabel}</Button>
                    {sentTo && <Typography variant="body2" color="text.secondary">Sent to {sentTo}.</Typography>}
                </Stack>
            )}
            <CodeField
                value={code}
                onChange={setCode}
                disabled={disabled}
                label={method === 'sms' ? 'Code from the text message' : 'Code from your authenticator app'}
            />
        </>
    );
}

function useSendCode(current, password, setFieldErrors, setError) {
    const dataAPI = React.useContext(DataAPIContext);
    const [sending, setSending] = React.useState(false);
    const send = async () => {
        setSending(true);
        setError(null);
        setFieldErrors({});
        try {
            const answer = await sendSmsCode(dataAPI, password);
            current.setSentTo(answer?.sent_to_masked || 'your phone');
            current.startResend(answer?.resend_after);
        } catch (err) {
            if (err.code === 'invalid_password') {
                setFieldErrors({ password: err.message });
            } else {
                setError(err.message);
            }
            if (err.code === 'sms_cooldown') {
                current.startResend(err.body?.retry_after);
            }
        }
        setSending(false);
    };
    return [sending, send];
}

function NewCodesDialog({ status, onClose, onCodes }) {
    const dataAPI = React.useContext(DataAPIContext);
    const current = useCurrentCode(status);
    const [password, setPassword] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [fieldErrors, setFieldErrors] = React.useState({});
    const [sending, send] = useSendCode(current, password, setFieldErrors, setError);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            const answer = await regenerateRecoveryCodes(dataAPI, current.method, current.code);
            onCodes(answer.recovery_codes);
            return;
        } catch (err) {
            current.setCode('');
            setError(err.message);
        }
        setBusy(false);
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="mfa-codes-title">
            <DialogTitle id="mfa-codes-title">Get new recovery codes</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    Your current recovery codes stop working at once. Confirm with a current code.
                </Typography>
                <CurrentCodeFields
                    current={current}
                    password={password}
                    passwordField={<PasswordField value={password} onChange={setPassword} error={fieldErrors.password} disabled={busy || sending} />}
                    sending={sending}
                    onSend={send}
                    disabled={busy}
                />
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !codeReady(current.code)}>Get new codes</Button>
            </DialogActions>
        </Dialog>
    );
}

function TurnOffDialog({ status, onClose, onTurnedOff }) {
    const dataAPI = React.useContext(DataAPIContext);
    const current = useCurrentCode(status);
    const [password, setPassword] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [fieldErrors, setFieldErrors] = React.useState({});
    const [sending, send] = useSendCode(current, password, setFieldErrors, setError);

    const submit = async () => {
        setBusy(true);
        setError(null);
        setFieldErrors({});
        try {
            await disableMfa(dataAPI, { password, method: current.method, code: current.code });
            onTurnedOff();
            return;
        } catch (err) {
            if (err.code === 'invalid_password') {
                setFieldErrors({ password: err.message });
            } else {
                current.setCode('');
                setError(err.message);
            }
        }
        setBusy(false);
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="mfa-off-title">
            <DialogTitle id="mfa-off-title">Turn off two-step sign-in</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    Your authenticator app, phone number and recovery codes are removed, and every dashboard session of
                    yours ends, this one too. You then sign in with your password alone.
                </Typography>
                <PasswordField value={password} onChange={setPassword} error={fieldErrors.password} disabled={busy || sending} />
                <CurrentCodeFields current={current} password={password} sending={sending} onSend={send} disabled={busy} />
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="outlined" color="error" onClick={submit} disabled={busy || !password || !codeReady(current.code)}>
                    Turn off
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ---- the page ---------------------------------------------------------------------------------------------

function FactorRow({ kind, devices, onSetUp }) {
    const set = devices.filter((d) => d.kind === kind);
    const Icon = kind === 'sms' ? SmsOutlinedIcon : PhoneIphoneOutlinedIcon;
    let detail = 'Not set up';
    if (set.length) {
        detail = kind === 'sms'
            ? set.map((d) => d.target_masked).filter(Boolean).join(', ') || 'Set up'
            : (fmt(set[0].created) ? `Set up ${fmt(set[0].created)}` : 'Set up');
    }
    let action = kind === 'sms' ? 'Set up text messages' : 'Set up an authenticator app';
    if (set.length) action = kind === 'sms' ? 'Change the phone number' : 'Replace the authenticator app';
    return (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1.5 }} role="group" aria-label={KIND_LABEL[kind]}>
            <Icon color={set.length ? 'primary' : 'disabled'} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {KIND_LABEL[kind]}{kind === 'totp' ? ' (recommended)' : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">{detail}</Typography>
            </Box>
            <Button variant={set.length ? 'text' : 'outlined'} aria-label={action} onClick={() => onSetUp(kind, set.length > 0)}>
                {set.length ? (kind === 'sms' ? 'Change number' : 'Replace') : 'Set up'}
            </Button>
        </Stack>
    );
}

function SecurityContent({ status, onChanged, onTokenRotated, onTurnedOff }) {
    const dataAPI = React.useContext(DataAPIContext);
    const { user, tourOperator } = React.useContext(ContextStore);
    const { enqueueSnackbar } = useSnackbar();
    const [dialog, setDialog] = React.useState(null);
    const [savingPrimary, setSavingPrimary] = React.useState(false);

    if (!status.password_sign_in) {
        return (
            <Alert severity="info">
                Your company signs in with single sign-on, so there is no password sign-in to protect here. Whether that
                sign-in asks for a second step is up to your company&apos;s identity provider.
            </Alert>
        );
    }

    const kinds = enrolledKinds(status);

    // The page reloads its status when the token changes (SecurityPage), with the new token.
    const onConfirmed = (result, kind) => {
        onTokenRotated(result);
        enqueueSnackbar(kind === 'sms' ? 'Text messages are set up.' : 'The authenticator app is set up.', { variant: 'success' });
        setDialog(result.recovery_codes?.length ? { kind: 'show_codes', codes: result.recovery_codes } : null);
    };

    const changePrimary = async (method) => {
        setSavingPrimary(true);
        try {
            onChanged(await setPrimaryMethod(dataAPI, method));
            enqueueSnackbar(`Sign-in asks for the ${METHOD_LABELS[method].toLowerCase()} code first.`, { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.message, { variant: 'error' });
        }
        setSavingPrimary(false);
    };

    return (
        <Stack spacing={2}>
            <Alert severity={status.policy?.mode === 'mandatory' || status.enrolled ? 'success' : 'info'}>{policyText(status)}</Alert>

            <Paper variant="outlined" sx={{ px: 2 }}>
                {['totp', 'sms'].map((kind, idx) => (
                    <React.Fragment key={kind}>
                        {idx > 0 && <Divider />}
                        <FactorRow kind={kind} devices={status.devices || []} onSetUp={(k, replacing) => setDialog({ kind: 'factor', factor: k, replacing })} />
                    </React.Fragment>
                ))}
            </Paper>

            {kinds.length > 1 && (
                <FormControl component="fieldset">
                    <FormLabel component="legend">At sign-in, ask first for</FormLabel>
                    <RadioGroup row value={status.primary_method || ''} onChange={(ev) => changePrimary(ev.target.value)}>
                        {kinds.map((k) => (
                            <FormControlLabel key={k} value={k} control={<Radio size="small" />} label={METHOD_LABELS[k]} disabled={savingPrimary} />
                        ))}
                    </RadioGroup>
                </FormControl>
            )}

            {status.enrolled && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>Recovery codes</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {status.recovery_codes_remaining} of 10 unused
                                {fmt(status.recovery_codes_generated_ts) ? ` · created ${fmt(status.recovery_codes_generated_ts)}` : ''}
                            </Typography>
                        </Box>
                        <Button variant="outlined" onClick={() => setDialog({ kind: 'new_codes' })}>Get new codes</Button>
                    </Stack>
                </Paper>
            )}

            {status.enrolled && !status.session_mfa && (
                <Typography variant="body2" color="text.secondary">
                    This session was signed in with your password only.
                </Typography>
            )}

            {status.can_disable && (
                <Box>
                    <Button variant="outlined" color="error" onClick={() => setDialog({ kind: 'turn_off' })}>Turn off two-step sign-in</Button>
                </Box>
            )}

            {dialog?.kind === 'factor' && (
                <FactorDialog
                    kind={dialog.factor}
                    replacing={dialog.replacing}
                    phoneRegion={status.phone_region}
                    onClose={() => setDialog(null)}
                    onConfirmed={onConfirmed}
                />
            )}
            {dialog?.kind === 'new_codes' && (
                <NewCodesDialog
                    status={status}
                    onClose={() => setDialog(null)}
                    onCodes={(codes) => {
                        setDialog({ kind: 'show_codes', codes });
                        onChanged();
                    }}
                />
            )}
            {dialog?.kind === 'turn_off' && <TurnOffDialog status={status} onClose={() => setDialog(null)} onTurnedOff={onTurnedOff} />}
            {dialog?.kind === 'show_codes' && (
                <Dialog open maxWidth="xs" fullWidth aria-label="Your recovery codes">
                    <DialogContent>
                        <RecoveryCodesStep
                            codes={dialog.codes}
                            account={[user?.email, tourOperator?.name].filter(Boolean).join(' · ')}
                            fileName={`touroptima-recovery-codes-${tourOperator?.code || 'dashboard'}.txt`}
                            doneLabel="Done"
                            onDone={() => setDialog(null)}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Stack>
    );
}

const SecurityPage = () => {
    const dataAPI = React.useContext(DataAPIContext);
    const { token, logout, replaceSessionToken, setMfaStatus } = React.useContext(ContextStore);
    const [state, setState] = React.useState({ loading: true, error: null, status: null });

    const load = React.useCallback(() => {
        setState((prev) => ({ ...prev, loading: !prev.status, error: null }));
        getMfaStatus(dataAPI)
            .then((status) => {
                setState({ loading: false, error: null, status });
                setMfaStatus(status);
            })
            .catch((error) => {
                setState({ loading: false, error, status: null });
                if (error.status === 404) {
                    setMfaStatus(null);
                }
            });
    }, [dataAPI, setMfaStatus]);

    // Again when the session's token changes: a confirm signs the session in again, and the reload must go out
    // with the new token (App.js's data provider reads it at render).
    React.useEffect(() => {
        load();
    }, [load, token]);

    const onChanged = (status) => {
        if (status) {
            setState({ loading: false, error: null, status });
            setMfaStatus(status);
        } else {
            load();
        }
    };

    const onTurnedOff = () => {
        noteSignOutReason(TURNED_OFF_NOTICE);
        logout();
    };

    const { loading, error, status } = state;
    return (
        <Box p={2} sx={{ maxWidth: 720 }}>
            <PageMeta title="Sign-in security" />
            <Typography variant="h6">Sign-in security</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Two-step sign-in: after your password, a code from an app on your phone or a text message.
            </Typography>
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            )}
            {!loading && error && error.status === 404 && (
                <Alert severity="info">Two-step sign-in is not available yet.</Alert>
            )}
            {!loading && error && error.status !== 404 && (
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
                    Could not load your sign-in security.
                </Alert>
            )}
            {!loading && status && (
                <SecurityContent
                    status={status}
                    onChanged={onChanged}
                    onTokenRotated={replaceSessionToken}
                    onTurnedOff={onTurnedOff}
                />
            )}
        </Box>
    );
};

export default SecurityPage;
